import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import '../styles/ComicMenu.css';
import SYMBOLS from '../utils/symbols';

const ComicMenu = ({ onStartGame }) => {
  const [isAnimated, setIsAnimated] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const { currentTheme, changeTheme, allThemes } = useTheme();

  useEffect(() => {
    // Staggered entrance animation
    setTimeout(() => setIsAnimated(true), 100);
    setTimeout(() => setShowContent(true), 500);
  }, []);

  const handleStyleChange = (themeId) => {
    changeTheme(themeId);
  };

  // Manga theme filtered out for now. To enable it change the following line to:
  // const themeList = Object.values(allThemes);
  const themeList = Object.values(allThemes).filter(t => t.id !== 'manga');

  return (
    <div className={`comic-menu ${isAnimated ? 'animated' : ''} theme-${currentTheme}`}>
      {/* Animated background */}
      <div className="menu-background">
        {/* Halftone pattern */}
        <div className="halftone-bg"></div>
        
        {/* Speed lines */}
        <div className="speed-lines-bg">
          {[...Array(20)].map((_, i) => (
            <div key={i} className={`speed-line-bg line-${i % 5}`}></div>
          ))}
        </div>

        {/* Floating ink spots */}
        <div className="ink-spots">
          <div className="ink-spot spot-1"></div>
          <div className="ink-spot spot-2"></div>
          <div className="ink-spot spot-3"></div>
        </div>
      </div>

      {/* Main content */}
      <div className={`menu-content ${showContent ? 'show' : ''}`}>
        {/* Logo/Title */}
        <div className="title-section">
          <div className="title-burst"></div>
          <h1 className="game-title">
            <span className="title-line-1">RIBBON</span>
            <span className="title-line-2">RIDER</span>
          </h1>
          <div className="title-subtitle">COMICS</div>
          <div className="title-tagline">An Interactive Graphic Novel</div>
        </div>

        {/* Comic book cover mockup */}
        <div className="cover-preview">
          <div className="cover-frame">
            <div className="cover-inner">
              <div className="cover-image">
                <div className="cover-silhouette"></div>
                <div className="cover-time-effect"></div>
              </div>
              <div className="cover-banner">
                <span>TRAVEL THROUGH TIME</span>
              </div>
              <div className="cover-price">{'\u2116'} 1</div>
            </div>
          </div>
        </div>

        {/* Mission briefing */}
        <div className="briefing-panel">
          <div className="panel-header">
            <span className="header-icon">{SYMBOLS.DIAMOND_BULLET}</span>
            <span className="header-text">MISSION BRIEFING</span>
            <span className="header-icon">{SYMBOLS.DIAMOND_BULLET}</span>
          </div>
          <div className="panel-content">
            <p>
              You are a <strong>time traveler</strong> tasked with going back in time to alter an event and change human history. 
              Make good choices{'\u2014'}the future depends on you.
            </p>
            <div className="rules-list">
              <div className="rule">
                <span className="rule-icon">{SYMBOLS.CROSS}</span>
                <span>You cannot reveal to anyone that you're from the future</span>
              </div>
              <div className="rule">
                <span className="rule-icon">{SYMBOLS.CROSS}</span>
                <span>You cannot sacrifice your life to complete the mission</span>
              </div>
            </div>
          </div>
        </div>

        {/* Style Cards Selector */}
        <div className="style-cards-section">
          <div className="style-cards-header">
            <span className="style-cards-label">CHOOSE YOUR STYLE</span>
          </div>
          
          <div className="style-cards-grid">
            {themeList.map((themeOption) => (
              <button
                key={themeOption.id}
                className={`style-card ${currentTheme === themeOption.id ? 'selected' : ''}`}
                onClick={() => handleStyleChange(themeOption.id)}
                aria-pressed={currentTheme === themeOption.id}
              >
                {/* Color preview swatch */}
                <div 
                  className="style-card-preview"
                  style={{ 
                    background: `linear-gradient(135deg, ${themeOption.preview.primary} 0%, ${themeOption.preview.secondary} 100%)`
                  }}
                >
                  {/* Checkmark for selected */}
                  {currentTheme === themeOption.id && (
                    <span className="style-card-check">{SYMBOLS.CHECK_HEAVY}</span>
                  )}
                </div>
                
                {/* Style name */}
                <span className="style-card-name">{themeOption.name}</span>
                
                {/* Style subtitle */}
                <span className="style-card-subtitle">{themeOption.subtitle}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Start button */}
        <button className="start-button" onClick={onStartGame}>
          <span className="button-bg"></span>
          <span className="button-text">
            <span className="text-main">BEGIN YOUR STORY</span>
            <span className="text-sub">Issue #1 Awaits</span>
          </span>
          <span className="button-burst"></span>
        </button>

        {/* Credits */}
        <div className="menu-footer">
          <div className="footer-line"></div>
          <div className="footer-credits">
            <span className="separator">{'\u2022'}</span>
            <span>by Ben</span>
            <span className="separator">{'\u2022'}</span>
          </div>
        </div>
      </div>

      {/* Page curl effect */}
      <div className="page-curl"></div>
    </div>
  );
};

export default ComicMenu;